/** The customer's account: details, places, cards, notifications, help and privacy. */

import { del, errorMessage, get } from "@taxi/mobile-web/api";
import { phone } from "@taxi/mobile-web/format";
import {
  BellIcon,
  BriefcaseIcon,
  CardIcon,
  DownloadIcon,
  HomeIcon,
  InfoIcon,
  LogOutIcon,
  MailIcon,
  PhoneIcon,
  ShieldIcon,
  TrashIcon,
  UserIcon,
} from "@taxi/mobile-web/icons";
import { InstallHint } from "@taxi/mobile-web/install";
import { isStandalone, usePush } from "@taxi/mobile-web/push";
import { navigate } from "@taxi/mobile-web/router";
import { Avatar, Button, Card, Notice, Row, Sheet, TopBar, useToast } from "@taxi/mobile-web/ui";
import { useState } from "react";
import { useMe, useSession } from "../lib/session";

const ACCOUNT_STATUS: Record<string, string> = {
  requested: "Asked for, waiting for the office",
  approved: "Approved",
  declined: "Not approved",
  removed: "Removed",
};

export function AccountScreen() {
  const me = useMe();
  const { firm, firmPhone, signOut } = useSession();
  const toast = useToast();
  const push = usePush("customer", firm.webPush.publicKey);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function togglePush() {
    try {
      if ((await push.toggle()) === "blocked")
        toast("Notifications are blocked for this site in your browser settings.");
    } catch (error) {
      toast(errorMessage(error), "error");
    }
  }

  async function downloadData() {
    try {
      const data = await get<unknown>("/customer/me/export");
      const url = URL.createObjectURL(
        new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `${firm.name.replace(/\W+/g, "-").toLowerCase()}-my-data.json`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (error) {
      toast(errorMessage(error), "error");
    }
  }

  async function deleteAccount() {
    setDeleting(true);
    setDeleteError(null);
    try {
      await del("/customer/me");
      await signOut();
    } catch (error) {
      setDeleteError(errorMessage(error));
      setDeleting(false);
    }
  }

  const pushSubtitle = !push.available
    ? "Not available in this browser"
    : push.support === "add-to-home-screen"
      ? "Add this app to your Home Screen first"
      : push.state === "on"
        ? "On: we'll tell you when your driver's on the way"
        : push.state === "blocked"
          ? "Blocked in your browser settings"
          : "Off";

  return (
    <div className="account">
      <TopBar title="Account" />
      <div className="screen-body">
        <Card className="profile-card">
          <Avatar name={me.name} size={64} />
          <div>
            <strong>{me.name}</strong>
            <span>{phone(me.phone)}</span>
            {me.email && <span>{me.email}</span>}
          </div>
        </Card>

        <h2 className="section-title">Your account</h2>

        <div className="row-group">
          <Row
            icon={<UserIcon />}
            title="Your details"
            subtitle="Name and email for receipts"
            onClick={() => navigate("/account/details")}
          />
          <Row
            icon={<HomeIcon />}
            title="Saved places"
            subtitle="Home, Work and favourites"
            onClick={() => navigate("/account/places")}
          />
          {firm.payments.card && (
            <Row
              icon={<CardIcon />}
              title="Payment cards"
              onClick={() => navigate("/account/cards")}
            />
          )}
          {firm.payments.account && (
            <Row
              icon={<BriefcaseIcon />}
              title="Pay on Account"
              subtitle={
                me.account ? ACCOUNT_STATUS[me.account.status] : "For businesses and regulars"
              }
              onClick={() => navigate("/account/pay-on-account")}
            />
          )}
          <Row
            icon={<BellIcon />}
            title="Notifications"
            subtitle={pushSubtitle}
            right={
              push.available && push.support === "supported" ? (
                <button
                  type="button"
                  role="switch"
                  aria-checked={push.state === "on"}
                  aria-label="Notifications"
                  className="switch"
                  disabled={push.state === "busy" || push.state === "blocked"}
                  onClick={togglePush}
                />
              ) : undefined
            }
          />
        </div>

        {!isStandalone() && (
          <InstallHint
            title="Keep us on your Home Screen"
            why="It opens in one tap, like an app, and can send you notifications."
          />
        )}

        <h2 className="section-title">Help</h2>
        <div className="row-group">
          <Row
            icon={<PhoneIcon />}
            title="Call us"
            subtitle={phone(firmPhone)}
            onClick={() => (window.location.href = `tel:${firmPhone}`)}
          />
          <Row
            icon={<MailIcon />}
            title="Email us"
            subtitle={firm.email}
            onClick={() => (window.location.href = `mailto:${firm.email}`)}
          />
          <Row
            icon={<InfoIcon />}
            title="Terms"
            onClick={() => window.open(firm.links.terms, "_blank", "noopener")}
          />
          <Row
            icon={<ShieldIcon />}
            title="Privacy policy"
            onClick={() => window.open(firm.links.privacy, "_blank", "noopener")}
          />
        </div>

        <h2 className="section-title">Your data</h2>
        <div className="row-group">
          <Row
            icon={<DownloadIcon />}
            title="Download my data"
            subtitle="Everything we hold about you"
            onClick={downloadData}
          />
          <Row
            icon={<TrashIcon />}
            title="Delete my account"
            danger
            onClick={() => setConfirmDelete(true)}
          />
        </div>

        <Button variant="secondary" onClick={() => void signOut()}>
          <LogOutIcon size={18} /> Sign out
        </Button>
        <p className="fine-print center">
          {firm.legalName}
          {firm.operatorLicenceNumber ? ` · Operator licence ${firm.operatorLicenceNumber}` : ""}
        </p>
      </div>

      <Sheet
        open={confirmDelete}
        title="Delete your account?"
        onClose={() => setConfirmDelete(false)}
      >
        <p>
          We'll remove your details, saved places and cards, and sign you out everywhere. We keep
          booking records for as long as the law requires, then remove your details from them too.
        </p>
        {deleteError && <Notice tone="error">{deleteError}</Notice>}
        <Button variant="danger" loading={deleting} onClick={deleteAccount}>
          Delete my account
        </Button>
        <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
          Keep my account
        </Button>
      </Sheet>
    </div>
  );
}
